import React, {useState} from 'react';
import {Card, Form} from 'antd';
import PropTypes from 'prop-types';

import PersonalInformationForm from './PersonalInformationForm';
import SearchPerson from '../SearchPerson';
import {messageType, showMessages} from '../../helpers/message';

/**
 * This component is imported into AccountPersonAdd container and is the first step of the StepsWrapper.
 * It houses the SearchPerson and PersonalInformationForm components. And provides a tab navigation bar to switch between them.
 * The input data (personStates) is passed up to AccountPersonAdd and used to conditionally submit data to the parent form.
*/
const AccountPersonAddInformation = ({personStates, handleFinish, existingPerson, setExistingPerson}) => {
    // Explode person states into single variables for cleanliness.
    const { person_id, set_person_id,
            first_name, set_first_name, last_name, set_last_name,
            birth_date, set_birth_date, user_email, set_user_email,
            password, set_password, confirm_password, set_confirm_password } = personStates;

    // If it is an existing person default to search tab else fill tab.
    const [activeTabKey, setActiveTabKey] = useState(existingPerson ? 'search' : 'fill');
    const [form] = Form.useForm();

    // Handler for switching between the search/fill tabs. Clear personID on switching tabs.
    const onTabChange = (key) => {
        setActiveTabKey(key);

        if (key === 'search'){
            setExistingPerson(true);
            set_person_id(null);
        } else if (key === 'fill') {
            setExistingPerson(false);
        }

        // Clears the form fields on tab change, prevents searching for a person but accidentally creating a clone.
        if (existingPerson && person_id){
            form.resetFields();
            set_first_name('');
            set_last_name('');
            set_birth_date(null);
            set_user_email('');
            set_password('');
            set_confirm_password('');
        }
    }

    // Handler for clicking the next button and proceeding to next step (AccountPersonAddReview).
    // Throw a warning if user tries to proceed without selecting a person.
    const onFinish = () => {
        if (existingPerson && !person_id) {
            showMessages('Please select a person.',  messageType.error)
        } else {
            handleFinish();
        }
    }

    // The following components are put into JSX elements to organize them neatly amongst the tab functionality.
    const searchPersonJSX =
        <SearchPerson
            key='search'
            personStates={personStates}
        />

    const formJSX =
        <Form
            key='form'
            form={form}
            onFinish={onFinish}
            id="account-person-add-information"
            name="account-person-add-information"
            className="account-person-add-information"
            layout="vertical"
        />

    const fillPersonJSX =
        <Form
            key='form'
            form={form}
            onFinish={onFinish}
            id="account-person-add-information"
            name="account-person-add-information"
            className="account-person-add-information"
            layout="vertical"
        >
            <PersonalInformationForm
                isBusiness={false}
                firstName={first_name}
                setFirstName={set_first_name}
                lastName={last_name}
                setLastName={set_last_name}
                birthDate={birth_date}
                setBirthDate={set_birth_date}
                email={user_email}
                setEmail={set_user_email}
                passwordRequired={false}
                password={password}
                setPassword={set_password}
                confirmPassword={confirm_password}
                setConfirmPassword={set_confirm_password}
            />
        </Form>

    // searchPersonJSX needs to be adjacent to formJSX to prevent a linter warning about nesting forms.
    const contentList = {
        search: [searchPersonJSX, formJSX],
        fill: fillPersonJSX
    };

    const tabList = [
          {key: 'search', tab: 'Select an existing person'},
          {key: 'fill', tab: 'Create a new person'}
    ];

    return (
        <>
            <Card
                headStyle={{backgroundColor: '#F5F5F5'}}
                bordered={false}
                className="account-person-add-information-card"
                tabList={tabList}
                activeTabKey={activeTabKey}
                onTabChange={(key) => {
                    onTabChange(key);
                }}
            >
                {contentList[activeTabKey]}
            </Card>
        </>
    )
}

AccountPersonAddInformation.propTypes = {
    personStates: PropTypes.object.isRequired,
    handleFinish: PropTypes.func.isRequired,
    existingPerson: PropTypes.bool.isRequired,
    setExistingPerson: PropTypes.func.isRequired,
};

export default AccountPersonAddInformation;